import { useEffect, useMemo, useState } from 'react'
import type { Collection, GraphNode, Locale } from '../data/schema'
import { copy, localize } from '../i18n'
import { TypeIcon } from './TypeIcon'
import { Icon } from './Icon'

type CollectionSearchItem = Pick<Collection, 'id' | 'title' | 'kind'> & { description?: Collection['description'] }
type SearchResult = { kind: 'node'; node: GraphNode } | { kind: 'collection'; collection: CollectionSearchItem }
type RankedSearchResult = SearchResult & { label: string; order: number; score: number }

interface SearchBoxProps {
  nodes: GraphNode[]
  collections?: CollectionSearchItem[]
  locale: Locale
  onSelect: (id: string, resultRank: number) => void
  onSelectCollection?: (id: string, resultRank: number) => void
}

export function SearchBox({ nodes, collections = [], locale, onSelect, onSelectCollection = () => undefined }: SearchBoxProps) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const t = copy[locale]
  const results = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase()
    if (!normalized) return []
    const matchScore = (value: string) => {
      const normalizedValue = value.toLocaleLowerCase()
      if (normalizedValue === normalized) return 0
      if (normalizedValue.startsWith(normalized)) return 1
      if (normalizedValue.includes(normalized)) return 2
      return Number.POSITIVE_INFINITY
    }
    const bestScore = (values: string[]) => Math.min(...values.map(matchScore))
    const nodeResults: RankedSearchResult[] = nodes.map((node, order) => ({
      kind: 'node' as const,
      node,
      label: localize(node.name, locale),
      order,
      score: bestScore([node.name.en, node.name['zh-Hant'], ...node.aliases]),
    })).filter((result) => Number.isFinite(result.score))
    const collectionTerms = (collection: CollectionSearchItem) => [
      collection.title.en,
      collection.title['zh-Hant'],
      ...(collection.description ? [collection.description.en, collection.description['zh-Hant']] : []),
    ]
    const collectionResults: RankedSearchResult[] = collections.map((collection, order) => ({
      kind: 'collection' as const,
      collection,
      label: localize(collection.title, locale),
      order,
      score: bestScore(collectionTerms(collection)),
    })).filter((result) => Number.isFinite(result.score))
    return [...nodeResults, ...collectionResults]
      .sort((left, right) => left.score - right.score
        || (left.kind === 'node' ? 0 : 1) - (right.kind === 'node' ? 0 : 1)
        || left.label.localeCompare(right.label, locale)
        || left.order - right.order)
      .slice(0, 7)
  }, [collections, locale, nodes, query])
  useEffect(() => setActiveIndex(results.length ? 0 : -1), [query, results.length])
  const resultId = (result: SearchResult) => result.kind === 'node' ? result.node.id : `collection-${result.collection.id}`
  const selectResult = (result: RankedSearchResult) => {
    const rank = results.indexOf(result) + 1
    if (result.kind === 'node') onSelect(result.node.id, rank)
    else onSelectCollection(result.collection.id, rank)
    setQuery('')
    setActiveIndex(-1)
  }
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!query || !results.length) {
      if (event.key === 'Escape') setQuery('')
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1 + results.length) % results.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (index - 1 + results.length) % results.length)
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      selectResult(results[activeIndex])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setQuery('')
      setActiveIndex(-1)
    }
  }

  return (
    <div className="search-wrap">
      <Icon name="search" size={18} />
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t.search}
        aria-label={t.search}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={Boolean(query)}
        aria-controls="mythweave-search-results"
        aria-activedescendant={activeIndex >= 0 && results[activeIndex] ? `search-option-${resultId(results[activeIndex])}` : undefined}
        onKeyDown={handleKeyDown}
        data-testid="search-input"
      />
      {query && (
        <div className="search-results" role="listbox" id="mythweave-search-results" aria-label={locale === 'en' ? 'Mythology search results' : '神話搜尋結果'}>
          {results.length ? results.map((result) => (
            <button
              key={resultId(result)}
              id={`search-option-${resultId(result)}`}
              onClick={() => selectResult(result)}
              onMouseEnter={() => setActiveIndex(results.indexOf(result))}
              role="option"
              aria-selected={activeIndex >= 0 && resultId(results[activeIndex]) === resultId(result)}
              tabIndex={-1}
            >
              {result.kind === 'node' ? <>
                <span className={`type-sigil ${result.node.type}`} aria-hidden="true"><TypeIcon type={result.node.type} /></span>
                <span><strong>{localize(result.node.name, locale)}</strong>{locale !== 'en' && <small>{result.node.name.en}</small>}</span>
              </> : <>
                <span className="type-sigil collection" aria-hidden="true"><Icon name="list" size={15} /></span>
                <span><strong>{localize(result.collection.title, locale)}</strong><small>{locale === 'en' ? 'Reference list' : '參考名單'}</small></span>
              </>}
              <Icon name="chevron" size={16} />
            </button>
          )) : <div className="search-empty">{t.noResults}</div>}
        </div>
      )}
    </div>
  )
}
